import { createContext, useContext, useCallback, useMemo } from "react";
import LanguageHelper from "../../helpers/LanguageHelper";
import { languageStorage } from "../../helpers/StorageHelper";
import ILanguage from "../../interfaces/ILanguage";
import { LanguageTypes } from "../../data/Types";
import useState from "../../hooks/useState";


interface ILanguageContext {
    language: ILanguage;
    handleChangeLanguage: (languageValue: LanguageTypes) => void;
    translate: (text: string, languageValue?: LanguageTypes) => string;
}

interface IStateValues {
    language: ILanguage;
}


const LanguageContext = createContext<ILanguageContext | undefined>(undefined);

const LanguageContextProvider = (props: React.PropsWithChildren) => {
    const state = useState<IStateValues>({
        language: languageStorage.get()
    });

    
    const _handleChangeLanguage = useCallback((languageValue: LanguageTypes) => {
        const language = languageStorage.validate(languageValue);
        
        languageStorage.set(language);
        state.update('language', language);
        // eslint-disable-next-line
    }, []);


    const handleTranslation = useCallback((text: string, languageValue?: LanguageTypes) => {
        languageValue = languageStorage.validate(languageValue ?? state.values.language.value).value;
        
        if (LanguageHelper.TranslationExists(text)) {
            return LanguageHelper.FindTranslation(text)[languageValue as LanguageTypes];
        }

        return text;
        // eslint-disable-next-line
    }, [state.values.language]);


    const data: ILanguageContext = useMemo(() => {
        return {
            language: state.values.language,
            handleChangeLanguage: _handleChangeLanguage,
            translate: handleTranslation
        };
        
        // eslint-disable-next-line
    }, [state.values]);

    return (
        <LanguageContext.Provider value={data}>
            {props.children}
        </LanguageContext.Provider>
    );
}


const useLanguageContext = () => {
    const context = useContext(LanguageContext);
    
    if (context === undefined) {
        throw new Error('useLanguageContext must be used within a LanguageContextProvider');
    }
    
    return context;
}


export {
    useLanguageContext
}

export default LanguageContextProvider;